import moment, {Moment} from 'moment';
import {
  CareService,
  CareServiceRepetition,
  UpdateScope,
} from '../../../../backend/interfaces/care-service';
import {PostCareService} from '../../../../backend/requests/post-care-service';
import {PutCareService} from '../../../../backend/requests/put-care-service';
import {CalendarData, CalendarDataEvent} from '../../../calendar/calendar';
import {ActivityTypeSchedule} from '../../../../types';
import {darkenColor, getContrastColor} from '../../../../utils/functions/color-transforms';
import {DEFAULT_SERVICE_SHIFT_COLOR} from '../modals/care-services-input-color';
import {CareServiceCreatingItem, CareServiceForm, CareServiceItem} from './care-service-item';

export function transformUserCareServices2CareServiceItems(
  shifts: CareService[]
): CareServiceItem[] {
  const output: CareServiceItem[] = [];

  for (let i = 0; i < shifts.length; i++) {
    const shift = shifts[i];
    output.push(createCareServiceItem(shift));
  }

  return output;
}

export function transformCareServicesItems2CalendarData(items: CareServiceItem[]): CalendarData {
  const events: CalendarDataEvent[] = [];
  let minDate: Moment = moment('07:00', 'HH:mm');

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    events.push({
      id: item.id,
      title: item.title ?? '',
      start: item.startDate.toDate(),
      end: item.endDate.toDate(),
      backgroundColor: item.color,
      borderColor: darkenColor(item.color, 20),
      textColor: getContrastColor(item.color),
      extendedProps: {
        item: item,
      },
    });

    if (item.startDate.hours() < minDate.hours()) {
      minDate = item.startDate.clone().startOf('hour');
    }
  }

  return {
    events,
    minTime: minDate.format('HH:mm:ss'),
  };
}

// ------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------
// ------------------------------------------------------------------------------------------------

export function createCareServiceItem(item: CareService): CareServiceItem {
  const startDate = moment(item.startsAt);
  const endDate = moment(item.endsAt);
  const isTwentyFourHours = endDate.diff(startDate, 'hours') === 24;

  return {
    id: item.id,
    title: item.title,
    color: item.foregroundColor ?? DEFAULT_SERVICE_SHIFT_COLOR,
    targetUserId: item.userId,
    entityId: item.entityId,
    twentyFourHours: isTwentyFourHours,
    startDate: startDate,
    endDate: endDate,
    repetition: item.repetition,
    responsibleUserIds: item.associatedCaregiversUserIds,
    notes: item.notes,
  };
}

export function createNewCareServiceItem(opts: {
  start: Moment;
  end: Moment;
}): CareServiceCreatingItem {
  const twentyFourHours = opts.end.diff(opts.start, 'hours') === 24;

  return {
    title: undefined,
    color: DEFAULT_SERVICE_SHIFT_COLOR,
    startDate: opts.start,
    endDate: opts.end,
    twentyFourHours: twentyFourHours,
    targetUserId: undefined,
    repetition: undefined,
    responsibleUserIds: [],
    notes: undefined,
  };
}

export function createPutCareServiceViaForm(
  form: CareServiceForm,
  updateScope: UpdateScope
): PutCareService {
  return {
    updateScope: updateScope,
    title: form.title,
    foregroundColor: form.color,
    startsAt: form.startDate.utc().toISOString(),
    endsAt: form.endDate.utc().toISOString(),
    repetition: form.repetition,
    userId: form.targetUserId,
    associatedCaregiversUserIds: form.responsibleUserIds,
    notes: form.notes,
  };
}

export function createPutCareServiceViaItem(
  item: CareServiceItem,
  updateScope: UpdateScope
): PutCareService {
  return {
    updateScope: updateScope,
    title: item.title,
    foregroundColor: item.color,
    startsAt: item.startDate.utc().toISOString(),
    endsAt: item.endDate.utc().toISOString(),
    repetition: item.repetition,
    userId: item.targetUserId,
    associatedCaregiversUserIds: item.responsibleUserIds,
    notes: item.notes,
  };
}

export function createPostCareService(form: CareServiceForm): PostCareService {
  return {
    title: form.title || undefined,
    foregroundColor: form.color,
    startsAt: form.startDate.utc().toISOString(),
    endsAt: form.endDate.utc().toISOString(),
    repetition: form.repetition,
    userId: form.targetUserId,
    associatedCaregiversUserIds: form.responsibleUserIds,
    notes: form.notes || undefined,
  };
}

export function transformActivityTypeScheduleToCareServiceRepetition(
  schedule: ActivityTypeSchedule
): CareServiceRepetition | null {
  if (!schedule.frequency || !schedule.frequencyValue) return null;

  return {
    frequencyType: schedule.frequency,
    frequencyValue: schedule.frequencyValue,
    weeklyRepetitionDays: schedule.frequency === 'weekly' ? schedule.days : undefined,
    monthlyRepetitionDay: schedule.frequency === 'monthly' ? 'day-of-month' : undefined,
    endType: schedule.endDate ? 'on-date' : undefined,
    endDate: schedule.endDate ? schedule.endDate : undefined,
    endAfterOccurrencesValue: undefined,
  };
}

export function transformCareServiceRepetitionToActivityTypeSchedule(
  repetition?: CareServiceRepetition | null
): string {
  if (!repetition) return '{}';

  if (repetition.frequencyType === 'yearly') {
    throw new Error('Yearly frequency type is not supported in ActivityTypeSchedule');
  }

  const schedule: ActivityTypeSchedule = {
    frequency: repetition.frequencyType,
    frequencyValue: repetition.frequencyValue,
    days: repetition.frequencyType === 'weekly' ? repetition.weeklyRepetitionDays : undefined,
    endDate: repetition.endType === 'on-date' ? repetition.endDate : undefined,
    endHour: undefined,
  };

  return JSON.stringify(schedule);
}
